import { ApplicationTypeEnum, IProductLimitedDTO, ProductTypeEnum } from '../../legacy/shared-types';
import { Region } from 'hooks/useRegions/types';
import { TimeTradingEnum } from './ApplicantDetailsPage/SalariedDirectorFields/types';
import {
  AffordabilityCalculatorForm,
  AffordabilityEvaluationDto,
  Applicant,
  CurrencyPeriodEnum,
  EmploymentTypesEnum,
  Income,
  IProductSelectOption,
} from './types';

const convertStringToFirstLetterUppercase = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLocaleLowerCase();

export const productsToOtionsFormatter = (products: IProductLimitedDTO[]): IProductSelectOption[] =>
  products
    .filter(product => product.isActive && product.applicationType === ApplicationTypeEnum.PURCHASE)
    .map(product => ({
      value: product.salesforceId,
      label: product.description,
      productTypeString: convertStringToFirstLetterUppercase(product.productType),
      productType: product.productType,
    }))
    .sort((a, b) => a.productTypeString.localeCompare(b.productTypeString));

export const defaultRegionValue = Region.EAST_MIDLANDS;

export const incomeDefaultValues: Income = {
  basicAnnualSalary: null,
  basicAnnualSalaryPreviousYear: null,
  basicAnnualSalaryRecentYear: null,
  bonus: null,
  bonusPeriod: null,
  commission: null,
  commissionPeriod: null,
  dividendsPreviousYear: null,
  dividendsRecentYear: null,
  employmentType: null,
  guaranteedEmployeeBenefits: null,
  guaranteedEmployeeBenefitsPeriod: null,
  netTaxableIncomePreviousYear: null,
  netTaxableIncomeRecentYear: null,
  overtime: null,
  overtimePeriod: null,
  timeTrading: null,
  timeRemainingMonths: null,
  timeRemainingYears: null,
};

export const applicantDefaultValues: Applicant = {
  primaryIncome: incomeDefaultValues,
  isSecondIncome: '',
  secondIncome: incomeDefaultValues,
  otherIncome: {
    maintenanceIncome: null,
    maintenanceIncomePeriod: null,
    pensionIncome: null,
    pensionIncomePeriod: null,
    rentalIncome: null,
    rentalIncomePeriod: null,
  },
  benefitsAndCredits: {
    childBenefit: null,
    universalTaxCredits: null,
    stateDisabilityAllowance: null,
  },
  isFilled: false,
};

export const affordabilityCalculatorFormDefaultValues: AffordabilityCalculatorForm = {
  financeDetails: {
    financingAmount: null,
    deposit: null,
    ftv: null,
    product: null,
    numberOfApplicants: null,
    dependents: null,
    region: null,
    term: {
      years: null,
      months: null,
    },
  },
  applicants: [],
  isApplicantsViewHidden: false,
  outgoings: {
    creditCommitments: {
      creditStoreCardsBalance: null,
      securedLoanRepayments: null,
      securedLoanRepaymentsPeriod: null,
      unsecuredLoanRepayments: null,
      unsecuredLoanRepaymentsPeriod: null,
    },
    expenditure: {
      childcare: null,
      childcarePeriod: null,
      travelExpenses: null,
      travelExpensesPeriod: null,
      maintenance: null,
      maintenancePeriod: null,
    },
  },
  results: [],
};

export const periodOptions = [
  {
    value: CurrencyPeriodEnum.perMonth,
    label: 'per month',
  },
  {
    value: CurrencyPeriodEnum.perYear,
    label: 'per year',
  },
];

export const nullFiller = (value: any) => (value === null || value === undefined || value === '' ? 0 : value);

const periodMultiplier = (period: string | null) => {
  if (period === null) return 1;

  return period === CurrencyPeriodEnum.perMonth ? 12 : 1;
};

const periodDivider = (period: string | null) => {
  if (period === null) return 1;

  return period === CurrencyPeriodEnum.perYear ? 12 : 1;
};

export const defineIfRequestedFinanceCase = (
  requestedFinanceOfProduct: number,
  financingAmount?: number | null,
): boolean => {
  return !!(financingAmount && requestedFinanceOfProduct === financingAmount);
};

export const convertEmploymentTypesToDto = (employmentType: EmploymentTypesEnum | null) => {
  switch (employmentType) {
    case EmploymentTypesEnum.payeEmployed:
      return 'PAYE employee';
    case EmploymentTypesEnum.salariedDirector:
      return 'Salaried director';
    case EmploymentTypesEnum.soleTrader:
      return 'Sole trader';
    case EmploymentTypesEnum.contractor:
      return 'Contractor';
    case EmploymentTypesEnum.partnership:
      return 'Partnership';
    default:
      return '';
  }
};

export const selfEmployedEmployments = [
  EmploymentTypesEnum.salariedDirector,
  EmploymentTypesEnum.soleTrader,
  EmploymentTypesEnum.contractor,
  EmploymentTypesEnum.partnership,
];

const getTimeInJob = (employmentType: EmploymentTypesEnum | null) => {
  if (!employmentType || selfEmployedEmployments.includes(employmentType)) return 0;

  return 12;
};

const getTimeTrading = (employmentType: EmploymentTypesEnum | null, timeTrading: string | null) => {
  if (!employmentType || !selfEmployedEmployments.includes(employmentType)) return 0;
  if (timeTrading === null) return 25;

  return timeTrading === TimeTradingEnum.lessThanTwoYears ? 23 : 25;
};

export const applicantDataFormatter = (applicant: Applicant) => {
  const primaryIncome: Income = applicant.primaryIncome;
  const secondIncome: Income = applicant.isSecondIncome === 'yes' ? applicant.secondIncome : incomeDefaultValues;

  return {
    isRetiringWithinTenYears: false,
    projectedRetirementPayeIncome: 0,
    isStandardResidency: true,
    primaryIncome: {
      basicIncome: nullFiller(primaryIncome.basicAnnualSalary),
      bonus: nullFiller(primaryIncome.bonus) * periodMultiplier(primaryIncome.bonusPeriod),
      commission: nullFiller(primaryIncome.commission) * periodMultiplier(primaryIncome.commissionPeriod),
      dividendsMostRecentYear: nullFiller(primaryIncome.dividendsRecentYear),
      dividendsPriorYear: nullFiller(primaryIncome.dividendsPreviousYear),
      employersAllowance:
        nullFiller(primaryIncome.guaranteedEmployeeBenefits) *
        periodMultiplier(primaryIncome.guaranteedEmployeeBenefitsPeriod),
      employmentType: convertEmploymentTypesToDto(primaryIncome.employmentType),
      isHighRiskSector: false,
      netTaxableIncomeMostRecentYear: nullFiller(primaryIncome.netTaxableIncomeRecentYear),
      netTaxableIncomePriorYear: nullFiller(primaryIncome.netTaxableIncomePreviousYear),
      overtime: nullFiller(primaryIncome.overtime) * periodMultiplier(primaryIncome.overtimePeriod),
      salaryMostRecentYear: nullFiller(primaryIncome.basicAnnualSalaryRecentYear),
      salaryPriorYear: nullFiller(primaryIncome.basicAnnualSalaryPreviousYear),
      timeInJob: getTimeInJob(primaryIncome.employmentType),
      timeRemainingOnContract:
        nullFiller(primaryIncome.timeRemainingMonths) + nullFiller(primaryIncome.timeRemainingYears) * 12,
      timeTrading: getTimeTrading(primaryIncome.employmentType, primaryIncome.timeTrading),
    },
    secondIncome: {
      basicIncome: nullFiller(secondIncome.basicAnnualSalary),
      bonus: nullFiller(secondIncome.bonus) * periodMultiplier(secondIncome.bonusPeriod),
      commission: nullFiller(secondIncome.commission) * periodMultiplier(secondIncome.commissionPeriod),
      dividendsMostRecentYear: nullFiller(secondIncome.dividendsRecentYear),
      dividendsPriorYear: nullFiller(secondIncome.dividendsPreviousYear),
      employersAllowance:
        nullFiller(secondIncome.guaranteedEmployeeBenefits) *
        periodMultiplier(secondIncome.guaranteedEmployeeBenefitsPeriod),
      employmentType: convertEmploymentTypesToDto(secondIncome.employmentType),
      isHighRiskSector: false,
      netTaxableIncomeMostRecentYear: nullFiller(secondIncome.netTaxableIncomeRecentYear),
      netTaxableIncomePriorYear: nullFiller(secondIncome.netTaxableIncomePreviousYear),
      overtime: nullFiller(secondIncome.overtime) * periodMultiplier(secondIncome.overtimePeriod),
      salaryMostRecentYear: nullFiller(secondIncome.basicAnnualSalaryRecentYear),
      salaryPriorYear: nullFiller(secondIncome.basicAnnualSalaryPreviousYear),
      timeInJob: getTimeInJob(secondIncome.employmentType),
      timeRemainingOnContract:
        nullFiller(secondIncome.timeRemainingMonths) + nullFiller(secondIncome.timeRemainingYears) * 12,
      timeTrading: getTimeTrading(secondIncome.employmentType, secondIncome.timeTrading),
    },
    otherIncome: {
      pensionIncome:
        nullFiller(applicant.otherIncome.pensionIncome) * periodMultiplier(applicant.otherIncome.pensionIncomePeriod),
      rentalIncome:
        nullFiller(applicant.otherIncome.rentalIncome) * periodMultiplier(applicant.otherIncome.rentalIncomePeriod),
      maintenanceReceipts:
        nullFiller(applicant.otherIncome.maintenanceIncome) *
        periodMultiplier(applicant.otherIncome.maintenanceIncomePeriod),
    },
  };
};

export const getProductDetails = (product: string | null) => {
  if (product === null) return { productType: null, productYears: null };

  const formattedProductDetails = product.toLowerCase().split('|');
  const formattedProductPeriod = formattedProductDetails[0].trim();
  const formattedProductType = formattedProductDetails[1].trim();
  const oneDigitRegex = /\d{1,2}/g;
  let productType: ProductTypeEnum | null;
  const productYears = formattedProductPeriod.match(oneDigitRegex)?.[0] || 0;

  if (formattedProductType.includes('discount')) {
    productType = ProductTypeEnum.DISCOUNT;
  } else if (formattedProductType.includes('fixed')) {
    productType = ProductTypeEnum.FIXED;
  } else productType = null;

  return { productType, productYears };
};

const getFinancingAmount = (financingAmount: number | string | null) =>
  financingAmount ? Number(financingAmount) : null;

export const formDataFormatter = (formData: AffordabilityCalculatorForm): AffordabilityEvaluationDto => {
  const { financeDetails, outgoings, applicants } = formData;
  const { productType, productYears } = getProductDetails(financeDetails.product);
  const monthsInitial = productYears ? nullFiller(productYears) * 12 : null;

  return {
    runBy: 'calculator',
    affordabilitySpecificInputs: {
      productType: productType,
      monthsInitial: monthsInitial,
      termInMonths: nullFiller(financeDetails.term.years) * 12 + nullFiller(financeDetails.term.months),
      financingAmount: getFinancingAmount(financeDetails.financingAmount),
      isAddingProductFees: false,
    },
    creditCommitments: {
      buyNowPayLaterMonthlyPaymentOnRevertDate: 0,
      buyNowPayLaterMonthlyPaymentOnSpotDate: 0,
      creditCardBalance: nullFiller(outgoings.creditCommitments.creditStoreCardsBalance),
      securedLoansMonthlyPaymentOnRevertDate: nullFiller(outgoings.creditCommitments.securedLoanRepayments),
      securedLoansMonthlyPaymentOnSpotDate: nullFiller(outgoings.creditCommitments.securedLoanRepayments),
      unsecuredLoansMonthlyPaymentOnRevertDate: 0,
      unsecuredLoansMonthlyPaymentOnSpotDate: nullFiller(outgoings.creditCommitments.unsecuredLoanRepayments),
    },
    numberOfDependants13OrUnder: Number(financeDetails.dependents),
    numberOfDependantsOver13: 0,
    declaredExpenditure: {
      basicEssentialLiving: 0,
      basicQualityOfLiving: 0,
      childcare: nullFiller(outgoings.expenditure.childcare) / periodDivider(outgoings.expenditure.childcarePeriod),
      councilTax: 0,
      csaMaintenance:
        nullFiller(outgoings.expenditure.maintenance) / periodDivider(outgoings.expenditure.maintenancePeriod),
      serviceChargeGroundRent: 0,
      other: 0,
      travel:
        nullFiller(outgoings.expenditure.travelExpenses) / periodDivider(outgoings.expenditure.travelExpensesPeriod),
    },
    benefitsAndCredits: {
      childBenefit: applicants.reduce(
        (acc, applicant) => acc + nullFiller(applicant?.benefitsAndCredits.childBenefit) * 13,
        0,
      ),
      childTaxCredit: 0,
      industrialInjuriesDisablementBenefit: 0,
      personalIndependencePayment: applicants.reduce(
        (acc, applicant) => acc + nullFiller(applicant?.benefitsAndCredits.stateDisabilityAllowance) * 13,
        0,
      ),
      workingTaxCredit: applicants.reduce(
        (acc, applicant) => acc + nullFiller(applicant?.benefitsAndCredits.universalTaxCredits) * 13,
        0,
      ),
    },
    application: {
      depositAmount: nullFiller(financeDetails.deposit),
      isDIPStage: false,
      isJointApplication: applicants.length > 1,
      isMarried: false,
      isRetiredHousehold: false,
      purchaseRegion: financeDetails.region ? Region[financeDetails.region] : defaultRegionValue,
    },
    applicants: applicants.map(applicant => (applicant ? applicantDataFormatter(applicant) : null)),
  };
};
